import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhX";
import "@phosphor-icons/webcomponents/PhCalendar";
import "@phosphor-icons/webcomponents/PhClock";
import "@phosphor-icons/webcomponents/PhStop";
import "@phosphor-icons/webcomponents/PhWarning";
import "ui-components/button/wow-button";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/textarea/textarea";
import "ui-components/form-elements/select/wow-select";
import "ui-components/media-uploader/media-uploader";
import "ui-components/modal/wow-modal";
import "ui-components/confirmation/wow-confirmation";
import { NotificationService } from "ui-components/notification";
import "../stream-stats/stream-stats";

import { styles } from "./edit-live-event-modal.css";
import { rpc } from "ui-common/services/rpc";

@customElement("edit-live-event-modal")
export class EditLiveEventModal extends LitElement {
  static override styles = [styles];

  @property({ type: String })
  eventId!: string;

  @property({ type: Array })
  featureFlags: string[] = [];

  @state()
  private eventTitle: string = "";

  @state()
  private description = "";

  @state()
  private contentTier: "free" | "premium" = "premium";

  @state()
  private feeEuros = "";

  @state()
  private scheduledDate = "";

  @state()
  private scheduledTime = "";

  @state()
  private maxAttendees = "";

  @state()
  private thumbnailFileId?: string;

  @state()
  private thumbnailUrl?: string;

  @state()
  private whatsappUrl = "";

  @state()
  private postBuyingVideoUrl = "";

  @state()
  private postBuyingVideoFileId?: string;

  @state()
  private ctaText = "";

  @state()
  private backgroundImageFileIds: string[] = [];

  @state()
  private backgroundImageUrls: string[] = [];

  @state()
  private eventStatus: string = "";

  @state()
  private isSubmitting = false;

  @state()
  private isEnding = false;

  @state()
  private errors: Record<string, string> = {};

  @state()
  private isLoadingEvent = false;

  @state()
  private showEndConfirmation = false;

  override connectedCallback() {
    super.connectedCallback();
    this.loadEventData();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
  }

  private async loadEventData() {
    if (!this.eventId) return;

    try {
      this.isLoadingEvent = true;

      const result = await rpc("get-live-event-by-id", { event_id: this.eventId });

      if (!result.event) {
        NotificationService.error("Event not found");
        return;
      }

      const event = result.event;

      // Pre-fill form fields
      this.eventTitle = event.title || "";
      this.description = event.description || "";
      this.contentTier = event.content_tier === "premium" ? "premium" : "free";
      this.feeEuros = event.fee_cents ? (event.fee_cents / 100).toFixed(2) : "";
      this.maxAttendees = event.max_attendees ? event.max_attendees.toString() : "";
      this.thumbnailFileId = event.thumbnail_file_id || undefined;
      this.thumbnailUrl = event.thumbnail_url || undefined;
      this.whatsappUrl = event.whatsapp_url || "";
      this.postBuyingVideoFileId = event.post_buying_video_file_id || undefined;
      this.postBuyingVideoUrl = event.post_buying_video_url || "";
      this.ctaText = event.cta_text || "";
      this.backgroundImageFileIds = event.background_image_file_ids || [];
      this.backgroundImageUrls = event.background_image_urls || [];
      this.eventStatus = event.status || "";

      // Parse date and time from ISO string (use local time consistently)
      if (event.scheduled_start_time) {
        const date = new Date(event.scheduled_start_time);
        // Format as YYYY-MM-DD for date input (local time)
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        this.scheduledDate = `${year}-${month}-${day}`;
        // Format as HH:mm for time input (local time)
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        this.scheduledTime = `${hours}:${minutes}`;
      }
    } catch (error) {
      console.error("Error loading event:", error);
      NotificationService.error("Failed to load event data");
    } finally {
      this.isLoadingEvent = false;
    }
  }

  private validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    // Check if event has started (scheduled time has passed)
    const hasEventStarted =
      this.scheduledDate && this.scheduledTime
        ? new Date(`${this.scheduledDate}T${this.scheduledTime}`).getTime() <= Date.now()
        : false;

    // Don't validate form if event is read-only (live or started)
    if (this.eventStatus === "live" || hasEventStarted) {
      return true;
    }

    if (!this.eventTitle || !this.eventTitle.trim()) {
      newErrors.title = "Title is required";
    }

    if (!this.description || !this.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!this.scheduledDate) {
      newErrors.date = "Date is required";
    }

    if (!this.scheduledTime) {
      newErrors.time = "Time is required";
    }

    if (this.scheduledDate && this.scheduledTime) {
      const scheduledDateTime = new Date(`${this.scheduledDate}T${this.scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        newErrors.datetime = "Scheduled time must be in the future";
      }
    }

    if (this.maxAttendees && this.maxAttendees.trim() !== "") {
      const numValue = Number(this.maxAttendees);
      if (isNaN(numValue) || numValue < 1) {
        newErrors.maxAttendees = "Max attendees must be a positive number";
      }
    }

    if (this.contentTier === "premium") {
      if (!this.feeEuros || !this.feeEuros.trim()) {
        newErrors.fee = "Price is required for premium events";
      } else {
        const numValue = parseFloat(this.feeEuros);
        if (isNaN(numValue) || numValue <= 0) {
          newErrors.fee = "Price must be greater than 0";
        } else if (numValue > 999) {
          newErrors.fee = "Price cannot exceed €999";
        }
      }
    }

    this.errors = newErrors;
    return Object.keys(newErrors).length === 0;
  }

  private async handleSubmit() {
    if (!this.validateForm()) {
      return;
    }

    this.isSubmitting = true;

    try {
      // Parse as local time to validate
      const scheduledDateTime = new Date(`${this.scheduledDate}T${this.scheduledTime}`);

      if (isNaN(scheduledDateTime.getTime())) {
        NotificationService.error("Invalid date or time format");
        this.isSubmitting = false;
        return;
      }

      if (scheduledDateTime <= new Date()) {
        NotificationService.error("Scheduled time must be in the future");
        this.isSubmitting = false;
        return;
      }

      const localDateTime = new Date(`${this.scheduledDate}T${this.scheduledTime}:00`);
      const isoString = localDateTime.toISOString();

      const updateParams = {
        event_id: this.eventId,
        title: (this.eventTitle || "").trim(),
        description: (this.description || "").trim(),
        content_tier: this.contentTier,
        fee_cents: this.contentTier === "premium" && this.feeEuros ? Math.round(parseFloat(this.feeEuros) * 100) : null,
        scheduled_start_time: isoString,
        max_attendees: this.maxAttendees ? Number(this.maxAttendees) : null,
        thumbnail_file_id: this.thumbnailFileId || null,
        whatsapp_url: this.whatsappUrl?.trim() || null,
        post_buying_video_file_id: this.postBuyingVideoFileId || null,
        cta_text: this.ctaText?.trim() || null,
        background_image_file_ids: this.backgroundImageFileIds.length > 0 ? this.backgroundImageFileIds : null,
      };

      const result = await rpc("update-live-event", updateParams);

      if (result && typeof result === "object" && "success" in result && result.success) {
        NotificationService.success("Live event updated successfully!");

        // Dispatch event to refresh the list
        this.dispatchEvent(
          new CustomEvent("event-updated", {
            detail: { event: (result as { event?: unknown }).event },
            bubbles: true,
          }),
        );

        // Close modal after a short delay
        setTimeout(() => {
          this.handleClose();
        }, 500);
      } else {
        const message =
          result && typeof result === "object" && "message" in result
            ? (result as { message?: string }).message || "Failed to update live event"
            : "Failed to update live event";
        NotificationService.error(message);
      }
    } catch {
      NotificationService.error("Failed to update live event");
    } finally {
      this.isSubmitting = false;
    }
  }

  private handleEndEventClick() {
    this.showEndConfirmation = true;
  }

  private handleCancelEnd() {
    this.showEndConfirmation = false;
  }

  private async handleConfirmEnd() {
    try {
      this.isEnding = true;
      this.showEndConfirmation = false;
      const result = await rpc("end-live-event", { event_id: this.eventId });

      if (result.success) {
        NotificationService.success("Event ended successfully");

        this.dispatchEvent(
          new CustomEvent("event-ended", {
            detail: { eventId: this.eventId },
            bubbles: true,
            composed: true,
          }),
        );

        setTimeout(() => {
          this.handleClose();
        }, 1000);
      } else {
        NotificationService.error("Failed to end event");
      }
    } catch (error) {
      console.error("Error ending event:", error);
      NotificationService.error("Failed to end event");
    } finally {
      this.isEnding = false;
    }
  }

  private handleClose() {
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true }));
  }

  private handleThumbnailUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.thumbnailFileId = fileId;
  }

  private handleVideoUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.postBuyingVideoFileId = fileId;
  }

  private handleBackgroundImageUpload(index: number, e: CustomEvent) {
    const { fileId, fileUrl } = e.detail;
    const newFileIds = [...this.backgroundImageFileIds];
    const newUrls = [...this.backgroundImageUrls];

    if (fileId) {
      newFileIds[index] = fileId;
      newUrls[index] = fileUrl || "";
    } else {
      newFileIds.splice(index, 1);
      newUrls.splice(index, 1);
    }

    this.backgroundImageFileIds = newFileIds.filter(Boolean);
    this.backgroundImageUrls = newUrls.filter(Boolean);
  }

  private handleFeeInput(e: Event) {
    const input = e.target as HTMLInputElement;
    let value = input.value;

    // Allow only numbers and dots (no commas)
    value = value.replace(/[^\d.]/g, "");

    // Allow only one dot
    const dotIndex = value.indexOf(".");
    if (dotIndex !== -1) {
      const beforeDot = value.substring(0, dotIndex);
      const afterDot = value.substring(dotIndex + 1).replace(/\./g, "");
      // Limit to 2 decimal places
      value = beforeDot + "." + afterDot.substring(0, 2);
    }

    this.feeEuros = value;
    input.value = value;
    this.errors = { ...this.errors, fee: "" };
  }

  private get hasBothLiveEventFlags(): boolean {
    return this.featureFlags.includes("open-live-events") && this.featureFlags.includes("paid-live-events");
  }

  private get hasBackgroundImagesFlag(): boolean {
    return this.featureFlags.includes("live-event-background-images");
  }

  protected override render() {
    const scheduledDateTime = `${this.scheduledDate}T${this.scheduledTime}`;

    // Check if scheduled time has passed
    const hasScheduledTimePassed =
      this.scheduledDate && this.scheduledTime
        ? new Date(`${this.scheduledDate}T${this.scheduledTime}`).getTime() <= Date.now()
        : false;

    const isLive = this.eventStatus === "live";
    const isEnded = this.eventStatus === "ended";

    // Show end button if event is actually live OR if scheduled time has passed (so creator can mark it as ended)
    const canEndEvent = isLive || (this.eventStatus === "scheduled" && hasScheduledTimePassed);

    return html`
      <wow-modal
        mobile-fullscreen
        ?disable-primary-button=${this.isSubmitting || isEnded || this.isLoadingEvent}
        @close=${this.handleClose}
        @footer-button-clicked=${this.handleSubmit}
      >
        <span slot="header">
          ${this.isLoadingEvent
            ? "Loading..."
            : isEnded
              ? "View ended event"
              : isLive
                ? "Event is Live"
                : "Edit Live Event"}
        </span>

        <div class="form-content">
          ${this.isLoadingEvent
            ? html`<div style="text-align: center; padding: 40px;">Loading event data...</div>`
            : html`
                <!-- Event Cover Image -->
                <media-uploader
                  context="cover_image"
                  label="Event cover image"
                  viewport="cover"
                  currentFileUrl=${this.thumbnailUrl}
                  @change=${this.handleThumbnailUpload}
                ></media-uploader>

                <!-- Title -->
                <wow-input
                  label="Event title"
                  placeholder="Enter event title"
                  .value=${this.eventTitle || ""}
                  @input=${(e: Event) => {
                    this.eventTitle = (e.target as HTMLInputElement).value || "";
                    this.errors = { ...this.errors, title: "" };
                  }}
                  ?error=${!!this.errors.title}
                  help-text=${this.errors.title || ""}
                  required
                ></wow-input>

                <!-- Description -->
                <wow-textarea
                  label="Description"
                  placeholder="Describe what this live event is about"
                  .value=${this.description || ""}
                  @input=${(e: Event) => {
                    this.description = (e.target as HTMLTextAreaElement).value || "";
                    this.errors = { ...this.errors, description: "" };
                  }}
                  ?error=${!!this.errors.description}
                  help-text=${this.errors.description || ""}
                  rows="3"
                  maxlength="750"
                  required
                ></wow-textarea>

                ${this.hasBothLiveEventFlags
                  ? html`
                      <wow-select
                        label="Access level"
                        .options=${[
                          { value: "free", label: "Free - Anyone can attend" },
                          { value: "premium", label: "Premium - Ticket required" },
                        ]}
                        .value=${this.contentTier}
                        @change=${(e: CustomEvent) => {
                          this.contentTier = e.detail.value;
                          this.errors = { ...this.errors, fee: "" };
                        }}
                      ></wow-select>
                    `
                  : nothing}
                ${this.contentTier === "premium"
                  ? html`
                      <wow-input
                        type="text"
                        placeholder="2.99"
                        label="Ticket price (€)"
                        inputmode="decimal"
                        pattern="^\\d+(\\.\\d{1,2})?$"
                        .value=${this.feeEuros}
                        @input=${this.handleFeeInput}
                        ?error=${!!this.errors.fee}
                        help-text=${this.errors.fee || "Price in euros (e.g., 2.99)"}
                        required
                      ></wow-input>
                    `
                  : nothing}

                <!-- Date and Time -->
                <div class="form-row">
                  <wow-input
                    type="date"
                    label="Event date"
                    .value=${this.scheduledDate}
                    @input=${(e: Event) => {
                      this.scheduledDate = (e.target as HTMLInputElement).value;
                      this.errors = { ...this.errors, date: "", datetime: "" };
                    }}
                    ?error=${!!this.errors.date}
                    help-text=${this.errors.date || (isEnded ? "Cannot change date of ended event" : "")}
                    ?disabled=${isEnded}
                    required
                  ></wow-input>

                  <wow-input
                    type="time"
                    label="Event time"
                    .value=${this.scheduledTime}
                    @input=${(e: Event) => {
                      this.scheduledTime = (e.target as HTMLInputElement).value;
                      this.errors = { ...this.errors, time: "", datetime: "" };
                    }}
                    ?error=${!!this.errors.time}
                    help-text=${this.errors.time || (isEnded ? "Cannot change time of ended event" : "")}
                    ?disabled=${isEnded}
                    required
                  ></wow-input>
                </div>

                ${this.errors.datetime ? html` <div class="error-message">${this.errors.datetime}</div> ` : nothing}

                <!-- Max Attendees -->
                <wow-input
                  type="number"
                  label="Max attendees (Optional)"
                  placeholder="Leave empty for unlimited"
                  .value=${this.maxAttendees}
                  @input=${(e: Event) => {
                    this.maxAttendees = (e.target as HTMLInputElement).value;
                    this.errors = { ...this.errors, maxAttendees: "" };
                  }}
                  ?error=${!!this.errors.maxAttendees}
                ></wow-input>

                <!-- WhatsApp URL -->
                <wow-input
                  type="url"
                  label="WhatsApp community URL (Optional)"
                  placeholder="https://chat.whatsapp.com/..."
                  .value=${this.whatsappUrl}
                  @input=${(e: Event) => {
                    this.whatsappUrl = (e.target as HTMLInputElement).value;
                  }}
                ></wow-input>

                <!-- Post-Purchase Media -->
                <media-uploader
                  context="cover_image"
                  label="Post-purchase media (Optional)"
                  viewport="post"
                  currentFileUrl=${this.postBuyingVideoUrl}
                  @change=${this.handleVideoUpload}
                ></media-uploader>

                <!-- CTA Text -->
                <wow-input
                  label="Ticket CTA text (Optional)"
                  placeholder="e.g., I don't wanna miss this"
                  .value=${this.ctaText}
                  @input=${(e: Event) => {
                    this.ctaText = (e.target as HTMLInputElement).value;
                  }}
                  maxlength="255"
                ></wow-input>

                <!-- Background Images -->
                ${this.hasBackgroundImagesFlag
                  ? html`
                      <div class="background-images-section">
                        <label class="section-label">Background images (Optional)</label>
                        <div class="background-images-grid">
                          ${[0, 1, 2, 3].map(
                            (index) => html`
                              <media-uploader
                                context="cover_image"
                                viewport="post"
                                currentFileUrl=${this.backgroundImageUrls[index] || ""}
                                @change=${(e: CustomEvent) => this.handleBackgroundImageUpload(index, e)}
                              ></media-uploader>
                            `,
                          )}
                        </div>
                      </div>
                    `
                  : nothing}

                <!-- End Event Section (show if event can be ended) -->
                ${canEndEvent
                  ? html`
                      <div class="end-event-section">
                        <div class="warning-banner">
                          <ph-warning size="20" weight="fill"></ph-warning>
                          <span
                            >${isLive
                              ? "This event is currently LIVE"
                              : "The scheduled time for this event has passed"}</span
                          >
                        </div>
                        <wow-button
                          variant="danger"
                          size="medium"
                          @click=${this.handleEndEventClick}
                          ?disabled=${this.isEnding}
                        >
                          <ph-stop size="18" weight="fill"></ph-stop>
                          ${this.isEnding ? "Ending..." : "End Event"}
                        </wow-button>
                      </div>
                    `
                  : nothing}

                <!-- Stream Statistics -->
                ${this.eventId && this.scheduledDate && this.scheduledTime
                  ? html`
                      <stream-stats .eventId=${this.eventId} .scheduledDateTime=${scheduledDateTime}></stream-stats>
                    `
                  : nothing}
              `}
        </div>

        ${!isEnded && !this.isLoadingEvent
          ? html`<span slot="footer-button-text">${this.isSubmitting ? "Updating..." : "Update Event"}</span>`
          : nothing}
      </wow-modal>
      ${this.showEndConfirmation
        ? html`
            <wow-confirmation header="End Live Event">
              <div class="delete-confirmation-content">
                <p>End this live event? The recording will remain available.</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmEnd}> End Event </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelEnd}> Cancel </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "edit-live-event-modal": EditLiveEventModal;
  }
}
